import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { auth, signOut } from '../config/firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NuclearReset = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  const handleReset = async () => {
    const userId = localStorage.getItem('user_id');
    const deviceId = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id');
    
    if (!userId && !deviceId) {
      toast.error('No user data found to reset');
      return;
    }

    setLoading(true);

    try {
      // Call backend nuclear reset
      const response = await fetch(`${BACKEND_URL}/api/auth/nuclear-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || deviceId,
          device_id: deviceId,
          confirm: true
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        
        // Sign out from Firebase
        try {
          await signOut(auth);
        } catch (e) {}

        // Clear ALL local storage
        const preservedDeviceId = localStorage.getItem('device_id');
        localStorage.clear();
        sessionStorage.clear();
        if (preservedDeviceId) localStorage.setItem('device_id', preservedDeviceId);

        // Clear cookies
        document.cookie.split(";").forEach((c) => {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        // Clear IndexedDB
        if (window.indexedDB) {
          try {
            const databases = await window.indexedDB.databases();
            databases.forEach((db) => {
              if (db.name) window.indexedDB.deleteDatabase(db.name);
            });
          } catch (e) {}
        }

        setDone(true);
        toast.success('All data deleted successfully!');
      } else {
        toast.error(data.detail || 'Reset failed');
      }
    } catch (error) {
      toast.error('Reset failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
        <div className="bg-[#1a1a2e] rounded-2xl border border-green-500/30 p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Reset Complete!</h1>
          <p className="text-white/60 mb-4">All your data has been deleted.</p>
          
          {result && (
            <div className="bg-black/30 rounded-lg p-4 mb-6 text-left">
              <p className="text-green-400 text-sm font-mono">
                Total records deleted: {result.total_deleted}
              </p>
              <ul className="text-white/50 text-xs mt-2 space-y-1">
                {Object.entries(result.deleted_counts || {}).map(([key, val]) => (
                  <li key={key}>• {key}: {val}</li>
                ))}
              </ul>
            </div>
          )}
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white font-semibold"
          >
            Start Fresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
      <div className="bg-[#1a1a2e] rounded-2xl border border-red-500/30 p-8 max-w-md w-full">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Nuclear Reset</h1>
            <p className="text-red-400 text-sm">Delete ALL Data</p>
          </div>
        </div>

        <div className="bg-red-500/10 rounded-xl border border-red-500/20 p-4 mb-6">
          <p className="text-white/80 mb-3">This will permanently delete:</p>
          <ul className="text-white/60 text-sm space-y-2">
            <li>• User account & profile</li>
            <li>• Email & phone data</li>
            <li>• All sessions</li>
            <li>• Birth chart & stories</li>
            <li>• Sage credits & conversations</li>
            <li>• Subscription & payment history</li>
            <li>• All cached audio files</li>
            <li>• Device mappings</li>
          </ul>
          <p className="text-red-400 mt-4 font-semibold text-sm">
            ⚠️ This action is IRREVERSIBLE!
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleReset}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Trash2 className="w-5 h-5" />
                Delete All
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NuclearReset;
